import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  createPlan,
  type CustomizationInput,
  getPlan,
  getPlanOptions,
  getPlans,
  type PlanInput,
  savePlanCustomization,
  setPlanActive,
  suggestCheckpoint,
  updatePlan,
} from '../../../api/safetyRounds';

/** The checklist language for the reader: the app is in bokmål or English */
export const useChecklistLanguage = () => {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith('en') ? 'en' : 'nb';
};

export const planKeys = {
  all: ['safety-rounds', 'plans'] as const,
  overview: (language: string) => ['safety-rounds', 'plans', 'overview', language] as const,
  options: (language: string) => ['safety-rounds', 'plans', 'options', language] as const,
  one: (id: number, language: string) => ['safety-rounds', 'plans', id, language] as const,
};

export const usePlansQuery = () => {
  const language = useChecklistLanguage();
  return useQuery({ queryKey: planKeys.overview(language), queryFn: () => getPlans(language) });
};

export const usePlanOptionsQuery = () => {
  const language = useChecklistLanguage();
  return useQuery({ queryKey: planKeys.options(language), queryFn: () => getPlanOptions(language) });
};

export const usePlanQuery = (id: number | null) => {
  const language = useChecklistLanguage();
  return useQuery({
    queryKey: planKeys.one(id ?? 0, language),
    queryFn: () => getPlan(id as number, language),
    enabled: id !== null,
  });
};

const useInvalidatingMutation = <T, R>(mutationFn: (input: T) => Promise<R>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: planKeys.all }),
  });
};

export const useCreatePlanMutation = () => useInvalidatingMutation((input: PlanInput) => createPlan(input));
export const useUpdatePlanMutation = (id: number) => useInvalidatingMutation((input: PlanInput) => updatePlan(id, input));
export const useSetPlanActiveMutation = (id: number) =>
  useInvalidatingMutation((active: boolean) => setPlanActive(id, active));

export const useSaveCustomizationMutation = (id: number) => {
  const language = useChecklistLanguage();
  return useInvalidatingMutation((input: CustomizationInput) => savePlanCustomization(id, input, language));
};

/** A suggestion to HR for the template, from the safety representative's own checklist */
export const useSuggestCheckpointMutation = (templateId: number) =>
  useMutation({
    mutationFn: (input: { categoryKey: string | null; text: string }) => suggestCheckpoint(templateId, input),
  });
